import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Map, Activity, Shield, BarChart3, ArrowRight, X, CheckCircle2, Sparkles, Crown, TrendingDown, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TIER_MARKUPS, AI_PRICES } from '@/lib/credit-config';

const STEPS = [
  {
    icon: Map,
    title: 'Connect Your Schemas',
    description: 'Map fields between your CRM and ERP systems with visual drag-and-drop. Boltway™ supports Salesforce, Oracle, SAP, Workday, and more.',
    action: 'Open Mapping Studio',
    path: '/studio',
    color: 'bg-primary/10 text-primary',
  },
  {
    icon: Shield,
    title: 'Explore Intelligence Library',
    description: 'Browse 60+ pre-built signals, triggers, and scores across 6 enterprise domains. Customize rules for your business logic.',
    action: 'Open Rules Engine',
    path: '/rules',
    color: 'bg-info/10 text-info',
  },
  {
    icon: Activity,
    title: 'Run Your First Evaluation',
    description: 'Paste a call transcript or use live mic to detect contradictions, extract entities, and fire triggers in real-time.',
    action: 'Open Decision Engine',
    path: '/engine',
    color: 'bg-success/10 text-success',
  },
  {
    icon: BarChart3,
    title: 'Review Impact',
    description: 'See how intelligence corrects CRM data across CEO, CFO, Sales, HR, and Collections views. Track ROI and data trust scores.',
    action: 'Open ROI Dashboard',
    path: '/dashboard',
    color: 'bg-warning/10 text-warning',
  },
];

export function OnboardingWizard({ onDismiss }: { onDismiss: () => void }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(() => {
    const saved = localStorage.getItem('sf-onboarding-step');
    return saved ? Math.min(parseInt(saved, 10), STEPS.length - 1) : 0;
  });
  const current = STEPS[step];

  const goToStep = (s: number) => {
    setStep(s);
    localStorage.setItem('sf-onboarding-step', String(s));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4">
      <Card className="w-full max-w-lg border-primary/20 shadow-2xl max-h-[90vh] overflow-y-auto">
        <CardContent className="p-0">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <span className="text-[14px] font-bold text-foreground">Welcome to Boltway™</span>
            </div>
            <button onClick={onDismiss} className="text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Step indicator */}
          <div className="flex gap-1 px-6 pt-4">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? 'bg-primary' : 'bg-muted'}`}
              />
            ))}
          </div>

          {/* Content */}
          <div className="px-6 py-6 space-y-4">
            <div className="flex items-start gap-4">
              <div className={`h-12 w-12 rounded-xl ${current.color} flex items-center justify-center shrink-0`}>
                <current.icon className="h-6 w-6" />
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">Step {step + 1} of {STEPS.length}</p>
                <h2 className="text-[18px] font-bold text-foreground mt-1">{current.title}</h2>
                <p className="text-[13px] text-muted-foreground mt-2 leading-relaxed">{current.description}</p>
              </div>
            </div>
          </div>

          {/* Upgrade nudge on last step */}
          {step === STEPS.length - 1 && (
            <div className="mx-6 mb-4 rounded-lg border border-primary/20 bg-primary/[0.03] p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Crown className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold text-foreground">Unlock Full Power</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Free users pay {TIER_MARKUPS.free}× per AI operation. Upgrade to Pro for {TIER_MARKUPS.pro}× — that's <span className="font-bold text-success">90% less</span> per evaluation.
              </p>
              <div className="flex gap-2 text-[10px]">
                <div className="flex items-center gap-1 text-muted-foreground">
                  <TrendingDown className="h-3 w-3 text-success" />
                  <span>Eval: ${AI_PRICES.free.evaluation} → ${AI_PRICES.pro.evaluation}</span>
                </div>
                <div className="flex items-center gap-1 text-muted-foreground">
                  <TrendingDown className="h-3 w-3 text-success" />
                  <span>Repair: ${AI_PRICES.free.repair} → ${AI_PRICES.pro.repair}</span>
                </div>
              </div>
              <Button
                size="sm"
                className="w-full gap-1.5 text-xs"
                onClick={() => { onDismiss(); navigate('/pricing'); }}
              >
                <Zap className="h-3 w-3" />
                View Plans — Save 90% on AI
              </Button>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-border bg-muted/20">
            <Button variant="ghost" size="sm" onClick={onDismiss} className="text-[12px]">
              Skip Tour
            </Button>
            <div className="flex items-center gap-2">
              {step > 0 && (
                <Button variant="outline" size="sm" onClick={() => goToStep(step - 1)} className="text-[12px]">
                  Back
                </Button>
              )}
              {step < STEPS.length - 1 ? (
                <Button size="sm" onClick={() => goToStep(step + 1)} className="text-[12px] gap-1">
                  Next
                  <ArrowRight className="h-3 w-3" />
                </Button>
              ) : (
                <Button size="sm" onClick={() => { localStorage.removeItem('sf-onboarding-step'); onDismiss(); navigate('/studio'); }} className="text-[12px] gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  Get Started
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
